import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { machineryService } from '../../../services/machineryService';
import Toast from '../../../components/Common/Toast';
import { getCurrentTimeLima } from '../../../utils/dateUtils';
import { sanitizeBitacoraReadingInput } from '../../../utils/bitacoraFormat';

const CreateEvent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const machineIdFromUrl = searchParams.get('machine_id') || '';
  const vehicleIdFromUrl = searchParams.get('vehicle_id') || '';
  const hasContext = !!(machineIdFromUrl || vehicleIdFromUrl);

  const [loading, setLoading] = useState(false);
  const [machines, setMachines] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [conductors, setConductors] = useState([]);
  const [eventTypesList, setEventTypesList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [formData, setFormData] = useState({
    event_date: new Date().toISOString().split('T')[0],
    event_time: getCurrentTimeLima(),
    record_type: machineIdFromUrl ? 'machine' : 'vehicle',
    machine_id: machineIdFromUrl || '',
    vehicle_id: vehicleIdFromUrl || '',
    hours: '',
    kilometers: '',
    operator: '',
    event_type: '',
    project_id: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!hasContext || (!machines.length && !vehicles.length)) return;
    setFormData(prev => ({
      ...prev,
      record_type: machineIdFromUrl ? 'machine' : 'vehicle',
      machine_id: machineIdFromUrl || '',
      vehicle_id: vehicleIdFromUrl || ''
    }));
  }, [hasContext, machineIdFromUrl, vehicleIdFromUrl, machines.length, vehicles.length]);

  const loadData = async () => {
    try {
      const res = await machineryService.getMachineryFormData();
      if (res.success && res.data) {
        setMachines(res.data.machines || []);
        setVehicles(res.data.vehicles || []);
        setConductors(res.data.conductors || []);
        setEventTypesList(Array.isArray(res.data.event_types) ? res.data.event_types : []);
        setProjects(Array.isArray(res.data.projects) ? res.data.projects : []);
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al cargar datos', type: 'error' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al cargar datos', type: 'error' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = (name === 'hours' || name === 'kilometers')
      ? sanitizeBitacoraReadingInput(value)
      : value;
    setFormData(prev => {
      const next = { ...prev, [name]: nextValue };
      if (name === 'record_type') {
        if (value === 'machine') {
          next.vehicle_id = '';
        } else if (value === 'vehicle') {
          next.machine_id = '';
        }
        next.hours = '';
        next.kilometers = '';
      }
      return next;
    });
  };

  // Configuración de qué se controla según la máquina / vehículo actual
  const isMachineContext = formData.record_type === 'machine';
  const selectedMachine = isMachineContext
    ? machines.find(m => String(m.id) === String(formData.machine_id))
    : null;
  const selectedVehicle = !isMachineContext
    ? vehicles.find(v => String(v.id) === String(formData.vehicle_id))
    : null;
  const configSource = isMachineContext ? selectedMachine : selectedVehicle;

  const allowHour =
    configSource && typeof configSource.control_hour_meter === 'boolean'
      ? configSource.control_hour_meter
      : true; // si no hay config todavía, mostrar ambos como antes

  const allowKm =
    configSource && typeof configSource.control_mileage === 'boolean'
      ? configSource.control_mileage
      : true;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isMachine = formData.record_type === 'machine';

    if (configSource && !allowHour && !allowKm) {
      setToast({
        isOpen: true,
        message: 'Este equipo no tiene configurado control de horómetro ni kilometraje.',
        type: 'error'
      });
      return;
    }

    const hasHours = allowHour && formData.hours !== '' && formData.hours != null;
    const hasKm = allowKm && formData.kilometers !== '' && formData.kilometers != null;

    const hasMachine = isMachine && formData.machine_id && (hasHours || hasKm);
    const hasVehicle = !isMachine && formData.vehicle_id && (hasHours || hasKm);
    if (!hasMachine && !hasVehicle) {
      setToast({ isOpen: true, message: isMachine ? 'Seleccione una máquina e ingrese horómetro y/o kilometraje' : 'Seleccione un vehículo e ingrese horómetro y/o kilometraje', type: 'error' });
      return;
    }
    setLoading(true);
    try {
      const data = {
        event_date: formData.event_date,
        event_time: formData.event_time || null,
        operator: formData.operator || null,
        notes: formData.notes || null,
        event_type: formData.event_type || null,
        project_id: formData.project_id && formData.project_id !== '' ? formData.project_id : null,
        machine_id: isMachine ? (formData.machine_id || null) : null,
        vehicle_id: !isMachine ? (formData.vehicle_id || null) : null,
        hours: allowHour && formData.hours !== '' ? formData.hours : null,
        kilometers: allowKm && formData.kilometers !== '' ? formData.kilometers : null
      };
      const response = await machineryService.createEvent(data);
      if (response.success) {
        setToast({ isOpen: true, message: 'Evento registrado correctamente', type: 'success' });
        const backUrl = hasContext ? (machineIdFromUrl ? `/machinery/events/lista?machine_id=${machineIdFromUrl}` : `/machinery/events/lista?vehicle_id=${vehicleIdFromUrl}`) : '/machinery/events/lista';
        setTimeout(() => navigate(backUrl), 1500);
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al registrar', type: 'error' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al registrar el evento', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const contextMachine = hasContext && formData.record_type === 'machine' ? machines.find(m => String(m.id) === String(formData.machine_id)) : null;
  const contextVehicle = hasContext && formData.record_type === 'vehicle' ? vehicles.find(v => String(v.id) === String(formData.vehicle_id)) : null;

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Nuevo Evento (Bitácora)</h1>
          <p className="text-gray-600 mt-1">
            {hasContext
              ? (formData.record_type === 'machine'
                ? 'Registre fecha, hora, horómetro y/o kilometraje para la máquina seleccionada.'
                : 'Registre fecha, hora, horómetro y/o kilometraje para el vehículo seleccionado.')
              : 'El evento puede ser de maquinaria o de vehículo; en ambos puede registrar horómetro y/o kilometraje.'}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {hasContext && (contextMachine || contextVehicle) && (
            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border-b border-teal-100 px-6 py-4">
              <p className="text-xs font-medium uppercase tracking-wider text-teal-700/80 mb-1">
                {formData.record_type === 'machine' ? 'Máquina' : 'Vehículo'}
              </p>
              <p className="text-lg font-semibold text-gray-900">
                {contextMachine ? `${contextMachine.code} — ${contextMachine.name}` : contextVehicle ? `${contextVehicle.code} — ${contextVehicle.name}` : ''}
              </p>
            </div>
          )}
          <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tipo de evento primero */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de evento</label>
              <select
                name="event_type"
                value={formData.event_type || ''}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Seleccionar tipo de evento</option>
                {eventTypesList.map((t) => (
                  <option key={t.id} value={t.name}>{t.name}</option>
                ))}
              </select>
            </div>

            {!hasContext && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de registro *</label>
                <select
                  name="record_type"
                  value={formData.record_type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="machine">Maquinaria</option>
                  <option value="vehicle">Vehículo</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">Maquinaria o vehículo según el equipo</p>
              </div>
            )}
            {/* Sección Operador / Conductor */}
            <div className="md:col-span-2 border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Operador (Conductor)</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Seleccionar operador / conductor</label>
                <select
                  name="operator"
                  value={formData.operator}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Seleccionar operador / conductor</option>
                  {conductors.map(c => <option key={c.id} value={c.name}>{c.name}{c.license ? ` (${c.license})` : ''}</option>)}
                </select>
              </div>
            </div>

            {/* Proyecto */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Proyecto</label>
              <select
                name="project_id"
                value={formData.project_id || ''}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Seleccionar proyecto</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Fecha y Hora del evento */}
            <div className="md:col-span-2 border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Detalles del evento</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha del evento *</label>
                  <input
                    type="date"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hora del evento</label>
                  <input
                    type="time"
                    name="event_time"
                    value={formData.event_time || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
            </div>

            {/* Horómetro / Kilometraje */}
            {formData.record_type === 'machine' && (
              <div className="md:col-span-2 border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Horómetro y kilometraje</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {!hasContext && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Máquina *</label>
                      <select
                        name="machine_id"
                        value={formData.machine_id}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">Seleccionar</option>
                        {machines.map(m => <option key={m.id} value={m.id}>{m.code} - {m.name}</option>)}
                      </select>
                    </div>
                  )}
                  {allowHour && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Horómetro (horas)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        name="hours"
                        value={formData.hours}
                        onChange={handleChange}
                        placeholder="Ej. 2450.50"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  )}
                  {allowKm && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Kilometraje (km)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        name="kilometers"
                        value={formData.kilometers}
                        onChange={handleChange}
                        placeholder="Ej. 125000.25"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">Ingrese al menos horómetro o kilometraje (o ambos). Puede usar hasta 2 decimales.</p>
              </div>
            )}
            {formData.record_type === 'vehicle' && (
              <div className="md:col-span-2 border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Horómetro y kilometraje</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {!hasContext && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Vehículo *</label>
                      <select
                        name="vehicle_id"
                        value={formData.vehicle_id}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">Seleccionar</option>
                        {vehicles.map(v => <option key={v.id} value={v.id}>{v.code} - {v.name}</option>)}
                      </select>
                    </div>
                  )}
                  {allowHour && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Horómetro (horas)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        name="hours"
                        value={formData.hours}
                        onChange={handleChange}
                        placeholder="Ej. 2450.50"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  )}
                  {allowKm && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Kilometraje (km)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        name="kilometers"
                        value={formData.kilometers}
                        onChange={handleChange}
                        placeholder="Ej. 125000.25"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">Ingrese al menos horómetro o kilometraje (o ambos). Puede usar hasta 2 decimales.</p>
              </div>
            )}

            {/* Notas */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Notas</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                placeholder="Observaciones del evento"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-gray-100 flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
            >
              {loading ? 'Guardando...' : 'Guardar evento'}
            </button>
            <button
              type="button"
              onClick={() => {
                const backUrl = hasContext ? (machineIdFromUrl ? `/machinery/events/lista?machine_id=${machineIdFromUrl}` : `/machinery/events/lista?vehicle_id=${vehicleIdFromUrl}`) : '/machinery/events/lista';
                navigate(backUrl);
              }}
              className="px-6 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
          </div>
          </div>
        </form>
      </div>
      <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, isOpen: false })} />
    </div>
  );
};

export default CreateEvent;
